import type { PioneerSlot } from "../types/pioneerSlot";

export type PioneerSlotOnboardingDisposition =
  | "absent"
  | "usable"
  | "used"
  | "terminal"
  | "recovery";

function toMillis(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (!value || typeof value !== "object") return null;

  if (
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  if (
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().getTime();
  }

  return null;
}

export function getPioneerSlotExpiryMillis(
  slot: PioneerSlot | null | undefined
): number | null {
  if (!slot) return null;

  for (const value of [
    slot.endsAt,
    slot.expiresAt,
    slot.validUntil,
    slot.subscriptionExpiresAt,
  ]) {
    const millis = toMillis(value);
    if (millis !== null) return millis;
  }

  return null;
}

export function getPioneerSlotOnboardingDisposition(
  slot: PioneerSlot | null | undefined,
  nowMs = Date.now()
): PioneerSlotOnboardingDisposition {
  if (!slot) return "absent";
  if (slot.businessReferenceMalformed) return "recovery";
  if (slot.status === "used") return "used";
  const expiryMs = getPioneerSlotExpiryMillis(slot);
  if (expiryMs !== null && expiryMs <= nowMs) {
    return slot.businessId ? "recovery" : "terminal";
  }
  if (slot.status === "expired" || slot.status === "cancelled") {
    return slot.businessId || slot.businessReferenceMalformed
      ? "recovery"
      : "terminal";
  }
  if (slot.status === "unknown") return "recovery";

  return !slot.businessId && slot.usedBusinesses < slot.maxBusinesses
    ? "usable"
    : "recovery";
}
